import React from 'react';
import isItemPortCall from '@shared/utils/isItemPortCall';
import { ConsumptionSetAttachedTo, PortActionTypes } from '@shared/utils/constants';
import type { GetVoyageItineraryResponse } from '@api/features/voyages/getVoyageItinerary';
import type { GetVesselDetailsResponse } from '@api/features/vessels/getVesselDetails';
import type { GetVoyageDetailsResponse } from '@api/features/voyages/getVoyageDetails';
import {
	ItineraryPortCallDto,
	ItinerarySeaPassageDto,
} from '@client/screens/estimates/details/helpers/types';
import PortCallSummaryTab from '@client/screens/fleet/VoyageDetailsScreen/components/ItineraryTab/tabs/PortCallSummaryTab';
import SeaPassageSummary from '@client/screens/fleet/VoyageDetailsScreen/components/ItineraryTab/tabs/SeaPassageSummary';
import ConditionTab from '@client/screens/fleet/VoyageDetailsScreen/components/ItineraryTab/tabs/ConditionTab';
import ConsumptionContainer from '@client/screens/fleet/VoyageDetailsScreen/components/ItineraryTab/tabs/ConsumptionContainer';
import { updateSeaPassage } from '@client/lib/api';
import ClaimsWrapper from '@client/screens/cargos/components/ClaimsModal/ClaimsWrapper';
import PortCallDeliveryRedeliveryTab from './PortCallDeliveryRedeliveryTab';
import TheShipReportsTab from './TheShipReportsTab';
import DaDeskTab from './DaDeskTab';

type Props = {
	itinerary: GetVoyageItineraryResponse | undefined;
	selectedEntry: ItinerarySeaPassageDto | ItineraryPortCallDto | undefined;
	voyageDetails: GetVoyageDetailsResponse;
	refreshDetails: () => void;
	refreshVessel: () => Promise<void> | void;
	setActiveTab: (tab: string) => void;
	vessel: GetVesselDetailsResponse | undefined;
	isConsMissingForSpeed: boolean;
	usesTheShip: boolean;
};

const shouldShowConditionTab = (
	selectedEntry: ItinerarySeaPassageDto | ItineraryPortCallDto,
) => isItemPortCall(selectedEntry);

const shouldShowConditionConsumptionTab = (
	selectedEntry: ItinerarySeaPassageDto | ItineraryPortCallDto,
) => !isItemPortCall(selectedEntry) &&
	(selectedEntry.estimated || (selectedEntry as ItinerarySeaPassageDto).inProgress);

const shouldShowDeliveryTab = (
	selectedEntry: ItinerarySeaPassageDto | ItineraryPortCallDto,
) => (isItemPortCall(selectedEntry) &&
	selectedEntry.actions.some((a) => a.event === PortActionTypes.DELIVERY));

const shouldShowRedeliveryTab = (
	selectedEntry: ItinerarySeaPassageDto | ItineraryPortCallDto,
) => (
	isItemPortCall(selectedEntry) &&
	selectedEntry.actions.some((a) => a.event === PortActionTypes.REDELIVERY)
);

const shouldShowLaytimeTab = (
	selectedEntry: ItinerarySeaPassageDto | ItineraryPortCallDto,
) => (
	isItemPortCall(selectedEntry) &&
	selectedEntry.actions.some(
		(pca) => pca.event === PortActionTypes.LOADING || pca.event === PortActionTypes.DISCHARGING,
	)
);

const shouldShowDaDeskTab = (
	selectedEntry: ItinerarySeaPassageDto | ItineraryPortCallDto,
) => (
	isItemPortCall(selectedEntry) &&
	(
		selectedEntry.daDeskExternalId != null ||
		selectedEntry.daDeskInterfaceUniqueReference != null
	)
);

const getTabList = ({
	selectedEntry,
	usesTheShip,
}: {
	selectedEntry: ItinerarySeaPassageDto | ItineraryPortCallDto;
	usesTheShip: boolean;
	isConsMissingForSpeed: boolean;
}) => {
	const tabs: Array<{
		tab: string | React.ReactElement;
		key: string;
	}> = [{
		tab: 'Summary',
		key: 'summary',
	}];

	if (shouldShowConditionTab(selectedEntry)) {
		tabs.push({
			tab: 'Condition',
			key: 'condition',
		});
	}

	if (usesTheShip) {
		tabs.push({
			tab: 'TheShip',
			key: 'tsReports',
		});
	}

	if (shouldShowConditionConsumptionTab(selectedEntry)) {
		tabs.push({
			tab: 'Condition / Consumption',
			key: 'condition',
		});
	}

	if (shouldShowDeliveryTab(selectedEntry)) {
		tabs.push({
			tab: 'Delivery (TC-Out)',
			key: 'delivery',
		});
	}

	if (shouldShowRedeliveryTab(selectedEntry)) {
		tabs.push({
			tab: 'Redelivery (TC-Out)',
			key: 'redelivery',
		});
	}

	if (shouldShowLaytimeTab(selectedEntry)) {
		tabs.push({
			tab: 'Laytime',
			key: 'laytime',
		});
	}

	if (shouldShowDaDeskTab(selectedEntry)) {
		tabs.push({
			tab: 'DA Desk',
			key: 'daDesk',
		});
	}

	return tabs;
};

const getTabContent = ({
	selectedEntry,
	refreshDetails,
	vessel,
	refreshVessel,
	isConsMissingForSpeed,
}: Omit<Props, 'selectedEntry' | 'usesTheShip'> & {
	selectedEntry: ItinerarySeaPassageDto | ItineraryPortCallDto;
}) => {
	if (isItemPortCall(selectedEntry)) {
		return {
			summary: (
				<PortCallSummaryTab />
			),
			condition: (
				<ConsumptionContainer vessel={vessel} refreshVessel={refreshVessel} />
			),
			tsReports: (<TheShipReportsTab selectedEntry={selectedEntry} />),
			delivery: (<PortCallDeliveryRedeliveryTab />),
			redelivery: (<PortCallDeliveryRedeliveryTab />),
			laytime: (<ClaimsWrapper portCallId={selectedEntry.id} />),
			daDesk: (<DaDeskTab vesselId={vessel?.id} portCall={selectedEntry} />),
		};
	}

	const makePrimary = async (id: number) => {
		await updateSeaPassage(vessel?.id!, selectedEntry.id, {
			activeConsumptionSetId: id,
		});
		await refreshDetails();
	};

	return {
		summary: (
			<SeaPassageSummary
				selectedEntry={selectedEntry}
				refreshDetails={refreshDetails}
				consumptionSets={vessel?.consumptionSets.filter(
					(cs => cs.attachedTo === ConsumptionSetAttachedTo.VESSEL || cs.voyageId === selectedEntry.voyageId)
				)}
			/>
		),
		condition: (selectedEntry.estimated || selectedEntry.inProgress) && (
			<ConditionTab
				selectedEntry={selectedEntry}
				refreshDetails={refreshDetails}
				vessel={vessel}
				refreshVessel={refreshVessel}
				isConsMissingForSpeed={isConsMissingForSpeed}
				makePrimary={makePrimary}
			/>
		),
		tsReports: (<TheShipReportsTab selectedEntry={selectedEntry} />),
	};
};

export const getTabs = ({
	selectedEntry,
	isConsMissingForSpeed,
	usesTheShip,
	...rest
}: Props) => {
	if (selectedEntry == null) {
		return {
			tabList: [],
			tabContent: [],
		};
	}

	const tabContent = getTabContent({
		selectedEntry,
		isConsMissingForSpeed,
		...rest,
	});

	const tabList = getTabList({
		selectedEntry,
		isConsMissingForSpeed,
		usesTheShip,
	});

	return {
		tabContent,
		tabList,
	};
};
